/**
 * Блок «Частые вопросы» на главной. Вопросы редактируются в панели
 * (/panel/admin/faq) и приходят из GET /api/faq. Пока видимых вопросов нет,
 * блок не рендерится вовсе.
 * answer_html санитизируется на бэкенде (sanitize-html) и повторно клиентом (DOMPurify).
 * Открыт всегда один вопрос: раскрытие другого сворачивает предыдущий.
 */
import React from 'react';
import DOMPurify from 'dompurify';
import { getPublicFaq, TPublicFaqItem } from '../../utils/adminApi';
import { rvDelay } from '../../utils/sectionReveal';
import { trackPreload } from '../../utils/preloader';
import styles from './faq.module.css';

// Вопросы между заходами на главную держим в памяти — экран не «доезжает»
let faqCache: TPublicFaqItem[] | null = null;

/** Загрузить вопросы заранее (пока человек на другой странице сайта). */
export function prefetchFaq(): void {
  if (faqCache) return;
  getPublicFaq().then(list => { faqCache = list; }).catch(() => undefined);
}

const Faq: React.FC = () => {
  const [items, setItems] = React.useState<TPublicFaqItem[]>(() => faqCache ?? []);
  // id открытого вопроса; по умолчанию первый, null — все свёрнуты
  const [openId, setOpenId] = React.useState<number | null | undefined>(undefined);

  React.useEffect(() => {
    let cancelled = false;
    trackPreload(getPublicFaq())
      .then(list => { faqCache = list; if (!cancelled) setItems(list); })
      .catch(() => { /* блок просто не показываем */ });
    return () => { cancelled = true; };
  }, []);

  if (items.length === 0) return null;

  return (
    <section className={styles.section} id="faq">
      <h2 className={`${styles.title} rv`} style={rvDelay(0.05)}>Частые вопросы</h2>
      <div className={`${styles.line} rv rv-line`} style={rvDelay(0.2)} />

      <div className={styles.list}>
        {items.map((item, i) => {
          const isOpen = (openId === undefined ? i === 0 : openId === item.id);
          const answerId = `faq-answer-${item.id}`;
          return (
            <div key={item.id} className={`${styles.item} ${isOpen ? styles.itemOpen : ''}`}>
              <h3 className={styles.heading}>
                <button
                  type="button"
                  className={styles.question}
                  aria-expanded={isOpen}
                  aria-controls={answerId}
                  onClick={() => setOpenId(isOpen ? null : item.id)}
                >
                  {item.question}
                  <span className={styles.icon} aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </span>
                </button>
              </h3>
              <div className={styles.answerWrap} id={answerId} role="region" aria-hidden={!isOpen}>
                <div className={styles.answerInner}>
                  <div
                    className={styles.answer}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.answer_html, { ADD_ATTR: ['target'] }) }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default Faq;
